/* eslint-disable react/display-name */
// QuestionForm.jsx
import  { forwardRef } from "react"; // Import forwardRef
import PropTypes from "prop-types";
import { Plus, Trash2, MinusCircle } from "lucide-react";
import ImageUploadComponent from "../ImageUploadComponent/ImageUploadComponent";
import "./QuestionForm.scss";

// Wrap the component with forwardRef
const QuestionForm = forwardRef((props, ref) => {
  const {
    question,
    index,
    onQuestionChange,
    onDeleteQuestion,
    onAddAnswer,
    onRemoveAnswer,
    imageData = {},
    readOnly = false,
    errors = {}
  } = props;

  const {
    questionImagePreview,
    explanationImagePreview,
    onQuestionImageChange,
    onExplanationImageChange,
    onRemoveQuestionImage,
    onRemoveExplanationImage,
  } = imageData;

  const handleTextChange = (field, value) => {
    if (readOnly) return;
    onQuestionChange(index, field, value);
  };

  const handleAnswerChange = (answerIndex, value) => {
    if (readOnly) return;
    onQuestionChange(index, "answers", { index: answerIndex, text: value });
  };

  const handleCorrectChange = (value) => {
    if (readOnly) return;
    onQuestionChange(index, "correct", value);
  };

  return (
    // Assign the forwarded ref to the main container div
    <div ref={ref} className={`training-question-detail ${readOnly ? 'read-only' : ''} ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`}>
      <div className="training-question-header">
        <div className="training-question-header-content">
          <div className="training-question-number">{index + 1}.</div>
          <div className="training-question-input-wrapper"> {/* PARENT WRAPPER for input and its error */}
            <input 
              type="text" 
              id={`question-text-${index}`}
              className="training-question-input" 
              placeholder="שאלה" 
              value={question.question || ""}
              onChange={(e) => handleTextChange("question", e.target.value)}
              readOnly={readOnly}
              aria-invalid={!!errors.question}
              aria-describedby={errors.question ? `question-error-${index}` : undefined}
            />
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={() => onDeleteQuestion(index)}
            className="training-button training-button-danger"
            type="button"
          >
            <Trash2 size={16} />
            מחק שאלה
          </button>
        )}
                    {/* Error message for THIS input, INSIDE the wrapper, AFTER the input */}
            {errors.question && (
              <p id={`question-error-${index}`} className="error-text" role="alert">
                {errors.question}
              </p>
            )}
      </div>
      
      {(questionImagePreview || !readOnly) && (
        <ImageUploadComponent
          imageUrl={questionImagePreview}
          onImageUpload={(e) => onQuestionImageChange(index, e)}
          onImageRemove={() => onRemoveQuestionImage(index)}
          label="תמונה לשאלה (אופציונלי)"
          placeholderText="העלאת תמונה לשאלה"
          disabled={readOnly}
        />
      )}

      <div className="training-question-options" id={`question-${index}-answers-section`}>
        {question.answers && question.answers.map((answer, answerIndex) => (
          <div
            key={answerIndex}
            className="training-question-option"
          >
            <input
              type="radio"
              id={`question-${index}-answer-radio-${answerIndex}`}
              name={`question-${index}-answer`}
              value={answerIndex} 
              checked={question.correct === answerIndex}
              onChange={() => handleCorrectChange(answerIndex)}
              className="training-question-radio"
              disabled={readOnly}
            />
            <input
              type="text"
              className="training-answer-input"
              placeholder={`תשובה ${answerIndex + 1}`}
              value={answer || ""}
              onChange={(e) => handleAnswerChange(answerIndex, e.target.value)}
              readOnly={readOnly}
            />
            {!readOnly && question.answers.length > 2 && (
              <button
                onClick={() => onRemoveAnswer(index, answerIndex)}
                className="training-button training-button-danger training-button-small"
                title="הסר תשובה"
                type="button"
              >
                <MinusCircle size={16} />
              </button>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            onClick={() => onAddAnswer(index)}
            className="training-button training-button-secondary training-button-small"
            style={{ marginTop: "8px" }}
            type="button"
          >
            <Plus size={16} />
            הוסף תשובה
          </button>
        )}
      </div>
      {/* Error message for general answer issues, AFTER the options block */}
      {errors.answersGeneral && (
        <p id={`question-answers-error-${index}`} className="error-text answers-general-error" role="alert">
          {errors.answersGeneral}
        </p>
      )}

      {(question.explanation || !readOnly) && (
        <div className="training-explanation-section">
          <label htmlFor={`question-${index}-explanation-input`} className="training-explanation-label">
            הסבר:
          </label>
          <textarea
            id={`question-${index}-explanation-input`}
            className="training-explanation-input"
            value={question.explanation || ""}
            onChange={(e) => handleTextChange("explanation", e.target.value)}
            placeholder="הכנס הסבר"
            readOnly={readOnly}
          />
        </div>
      )}
      
      {(explanationImagePreview || !readOnly) && (
        <ImageUploadComponent
          imageUrl={explanationImagePreview}
          onImageUpload={(e) => onExplanationImageChange(index, e)}
          onImageRemove={() => onRemoveExplanationImage(index)}
          label="תמונה להסבר (אופציונלי)"
          placeholderText="העלאת תמונה להסבר"
          disabled={readOnly}
        />
      )}
    </div>
  );
});

QuestionForm.propTypes = {
  question: PropTypes.shape({
    question: PropTypes.string,
    answers: PropTypes.arrayOf(PropTypes.string),
    correct: PropTypes.number,
    explanation: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  onQuestionChange: PropTypes.func.isRequired,
  onDeleteQuestion: PropTypes.func.isRequired,
  onAddAnswer: PropTypes.func.isRequired,
  onRemoveAnswer: PropTypes.func.isRequired,
  imageData: PropTypes.shape({
    questionImagePreview: PropTypes.string,
    explanationImagePreview: PropTypes.string,
    onQuestionImageChange: PropTypes.func,
    onExplanationImageChange: PropTypes.func,
    onRemoveQuestionImage: PropTypes.func,
    onRemoveExplanationImage: PropTypes.func
  }),
  readOnly: PropTypes.bool,
  errors: PropTypes.shape({
    question: PropTypes.string,
    answersGeneral: PropTypes.string
  })
};

export default QuestionForm;